import { stripUndefinedDeep } from './stripUndefinedDeep';

// --------------------
// Shared helpers
// --------------------
const cleanStr = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).trim();
    if (!s || s.toLowerCase() === "undefined" || s.toLowerCase() === "null") return "";
    return s;
};

const parseNum = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
};

const toISO = (v) => {
    if (!v) return null;
    try {
        // Firestore Timestamp-like
        if (typeof v === "object" && typeof v.toDate === "function") {
            return v.toDate().toISOString();
        }
        // millis / seconds
        if (typeof v === "number") {
            // heuristic: if seconds, convert to ms
            const ms = v < 10_000_000_000 ? v * 1000 : v;
            return new Date(ms).toISOString();
        }
        // string / date
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) return d.toISOString();
        return null;
    } catch {
        return null;
    }
};

const validateCoords = (c) => {
    if (!c) return null;
    const lat = parseNum(c.lat);
    const lng = parseNum(c.lng);
    if (lat !== null && lng !== null) return { lat, lng };
    return null;
};

const getAddressString = (main, obj, alt1, alt2, alt3) => {
    if (main && typeof main === "string" && main.trim().length > 0 && main !== "unknown_address") return main.trim();
    if (obj && typeof obj.address === "string" && obj.address.trim().length > 0) return obj.address.trim();
    if (obj && typeof obj.add === "string" && obj.add.trim().length > 0) return obj.add.trim();
    if (obj && typeof obj.formatted_address === "string" && obj.formatted_address.trim().length > 0) return obj.formatted_address.trim();
    if (obj && typeof obj.description === "string" && obj.description.trim().length > 0) return obj.description.trim();
    if (obj && typeof obj.name === "string" && obj.name.trim().length > 0) return obj.name.trim();
    if (alt1 && typeof alt1 === "string" && alt1.trim().length > 0) return alt1.trim();
    if (alt2 && typeof alt2 === "string" && alt2.trim().length > 0) return alt2.trim();
    if (alt3 && typeof alt3 === "string" && alt3.trim().length > 0) return alt3.trim();
    if (typeof obj === "string" && obj.trim().length > 0 && obj !== "unknown_address") return obj.trim();
    const coords = obj?.coords || (obj?.lat && obj?.lng ? obj : null);
    if (coords && coords.lat !== undefined && coords.lng !== undefined) {
        return `${Number(coords.lat).toFixed(5)}, ${Number(coords.lng).toFixed(5)}`;
    }
    return "unknown_address";
};

/**
 * Normalizes any booking object (legacy or new) to Booking Canonical.
 * RULES (strict):
 * - Payment mode is ONLY: ONLINE or ONBOARD
 * - ONLINE provider is ONLY: paypal|stripe|revolut|squareup
 * - ONBOARD provider MUST be null
 * - No card_legacy, no cash/card split for onboard
 */
export const normalizeBookingPayload = (raw) => {
    if (!raw) return null;

    // --------------------
    // A) Addresses (canonical)
    // --------------------
    const pickupAddressStr = getAddressString(
        raw.pickupAddress,
        raw.pickup,
        raw.trip_details?.pickup_address,
        raw.pickup?.description,
        raw.pickup?.add
    );

    const dropAddressStr = getAddressString(
        raw.dropAddress,
        raw.drop,
        raw.trip_details?.drop_address,
        raw.drop?.description,
        raw.drop?.add
    );

    const pickupCoords = validateCoords(raw.pickup?.coords || raw.pickup);
    const pickupObj = {
        address: pickupAddressStr,
        add: pickupAddressStr,
        description: pickupAddressStr,
        coords: pickupCoords,
        lat: pickupCoords?.lat || null,
        lng: pickupCoords?.lng || null
    };

    const dropCoords = validateCoords(raw.drop?.coords || raw.drop);
    const dropObj = {
        address: dropAddressStr,
        add: dropAddressStr,
        description: dropAddressStr,
        coords: dropCoords,
        lat: dropCoords?.lat || null,
        lng: dropCoords?.lng || null
    };

    // --------------------
    // Waypoints normalization (if present)
    // --------------------
    const rawWaypoints = (Array.isArray(raw.waypoints) && raw.waypoints.length > 0)
        ? raw.waypoints
        : (Array.isArray(raw.drop?.waypoints) && raw.drop.waypoints.length > 0)
            ? raw.drop.waypoints
            : null;

    let waypointsCanonical = undefined;
    if (rawWaypoints && rawWaypoints.length > 0) {
        const cleanedWps = rawWaypoints.map(wp => {
            if (!wp) return null;
            const address = getAddressString(wp.address, wp, wp.add, wp.description, wp.formatted_address);
            const coords = validateCoords(wp.coords || wp);
            return {
                lat: coords?.lat ?? (parseNum(wp.lat) ?? null),
                lng: coords?.lng ?? (parseNum(wp.lng) ?? null),
                add: address,
                address: address,
                description: address,
                coords: coords || null,
                source: cleanStr(wp.source) || "search"
            };
        }).filter(wp => wp !== null && (wp.add !== "unknown_address" || wp.coords !== null));
        if (cleanedWps.length > 0) {
            waypointsCanonical = cleanedWps;
            dropObj.waypoints = cleanedWps;
        }
    }

    // --------------------
    // B) Round trip normalization
    // --------------------
    const isReturnLeg = Boolean(
        raw.isReturn === true ||
        raw.leg === "return" ||
        (typeof raw.reference === "string" && raw.reference.endsWith("-R"))
    );
    const isRoundTrip = !isReturnLeg && (
        raw.returnTrip?.enabled === true ||
        raw.instructionData?.tripType === "round_trip" ||
        raw.tripType === "round_trip" ||
        Boolean(raw.bundleId) ||
        Boolean(raw.linkedBookingId) ||
        (typeof raw.reference === "string" && raw.reference.endsWith("-O")) ||
        !!raw.instructionData?.returnDate
    );

    let returnTripCanonical = null;

    if (isReturnLeg) {
        returnTripCanonical = {
            ...(raw.returnTrip || {}),
            enabled: false,
            tripdate: toISO(raw.returnTrip?.tripdate || raw.returnTripDate || raw.tripdate),
            pickupAddress: cleanStr(raw.returnTrip?.pickupAddress || raw.pickupAddress) || "unknown_address",
            dropAddress: cleanStr(raw.returnTrip?.dropAddress || raw.dropAddress) || "unknown_address",
            linkedBookingId: raw.returnTrip?.linkedBookingId || raw.linkedBookingId || null,
            reference: raw.returnTrip?.reference || null,
        };
    } else if (isRoundTrip) {
        const rDate =
            raw.returnTrip?.tripdate ||
            raw.returnTrip?.date ||
            raw.instructionData?.returnDate ||
            raw.returnDate;

        const rPickup =
            raw.returnTrip?.pickupAddress ||
            raw.returnTrip?.pickup?.address ||
            dropAddressStr;

        const rDrop =
            raw.returnTrip?.dropAddress ||
            raw.returnTrip?.drop?.address ||
            pickupAddressStr;

        const rPickupCoords = raw.returnTrip?.pickup?.coords || dropObj.coords;
        const rDropCoords = raw.returnTrip?.drop?.coords || pickupObj.coords;

        returnTripCanonical = {
            ...(raw.returnTrip || {}),
            enabled: true,
            tripdate: toISO(rDate),
            pickupAddress: cleanStr(rPickup) || "unknown_address",
            dropAddress: cleanStr(rDrop) || "unknown_address",
            pickup: { address: cleanStr(rPickup) || "unknown_address", coords: rPickupCoords },
            drop: { address: cleanStr(rDrop) || "unknown_address", coords: rDropCoords },
            // [FIX] Do NOT fallback to instructionData - that's for the OUTBOUND trip
            flightNumber: raw.returnTrip?.flightNumber || null,
            trainNumber: raw.returnTrip?.trainNumber || null,
            // [NEW] Pickup travel info for return trip
            pickupFlightNumber: raw.returnTrip?.pickupFlightNumber || null,
            pickupTrainNumber: raw.returnTrip?.pickupTrainNumber || null,
            linkedBookingId: raw.returnTrip?.linkedBookingId || raw.linkedBookingId || null,
            reference: raw.returnTrip?.reference || null,
        };
    } else {
        returnTripCanonical = { enabled: false };
    }

    // --------------------
    // C) Booker vs Passenger (customer)
    // --------------------
    const isBookingForOther = !!(
        raw.is_booking_for_other ||
        raw.instructionData?.bookingFor === "other" ||
        (raw.booker_details && raw.customer && (raw.booker_details.email !== raw.customer.email))
    );

    const rawBooker = raw.booker_details || {};
    const rawPassenger = (raw.customer && typeof raw.customer === 'object') ? raw.customer : (raw.userDetails || raw.passengerInfo || {});

    // [FIX] Extract name pieces from root customer_name if Passenger/Booker pieces are missing
    let passFirst = cleanStr(rawPassenger.firstName);
    let passLast = cleanStr(rawPassenger.lastName);
    if (!passFirst && !passLast && raw.customer_name) {
        const parts = String(raw.customer_name).trim().split(/\s+/);
        passFirst = parts[0] || "";
        passLast = parts.slice(1).join(" ") || "";
    }

    const candidateUid = cleanStr(
        raw.customer_uid ||
        rawPassenger.uid ||
        rawBooker.uid ||
        raw.uid ||
        raw.customer?.uid ||
        raw.userDetails?.uid ||
        (typeof raw.customer === 'string' ? raw.customer : "")
    );

    const booker_details = {
        firstName: cleanStr(rawBooker.firstName || (isBookingForOther ? "" : passFirst)),
        lastName: cleanStr(rawBooker.lastName || (isBookingForOther ? "" : passLast)),
        email: cleanStr(rawBooker.email || (isBookingForOther ? "" : (rawPassenger.email || raw.customer_email))),
        mobile: cleanStr(
            rawBooker.mobile ||
            rawBooker.phone ||
            (isBookingForOther ? "" : (rawPassenger.phone || rawPassenger.mobile || raw.customer_contact))
        ),
        uid: cleanStr(rawBooker.uid || raw.uid || candidateUid) || "guest"
    };

    const customer = {
        firstName: passFirst || cleanStr(isBookingForOther ? "" : rawBooker.firstName),
        lastName: passLast || cleanStr(isBookingForOther ? "" : rawBooker.lastName),
        email: cleanStr(rawPassenger.email || rawPassenger.customer_email || raw.customer_email || (isBookingForOther ? "" : rawBooker.email)),
        phone: cleanStr(rawPassenger.phone || rawPassenger.mobile || raw.customer_contact || (isBookingForOther ? "" : rawBooker.mobile)),
        uid: cleanStr(rawPassenger.uid || (typeof raw.customer === 'string' ? raw.customer : "") || candidateUid || (isBookingForOther ? "" : booker_details.uid)) || "guest"
    };

    // If NOT booking for other => keep identical
    if (!isBookingForOther) {
        if (!customer.firstName) customer.firstName = booker_details.firstName;
        if (!customer.lastName) customer.lastName = booker_details.lastName;
        if (!customer.email) customer.email = booker_details.email;
        if (!customer.phone) customer.phone = booker_details.mobile;

        if (!booker_details.firstName) booker_details.firstName = customer.firstName;
        if (!booker_details.lastName) booker_details.lastName = customer.lastName;
        if (!booker_details.email) booker_details.email = customer.email;
        if (!booker_details.mobile) booker_details.mobile = customer.phone;
    }

    // --------------------
    // D) Instruction data (tripType)
    // --------------------
    const instructionData = raw.instructionData || {};
    const effectiveTripType = instructionData.tripType || raw.tripType;

    if (isRoundTrip) instructionData.tripType = "round_trip";
    else if (effectiveTripType === "hourly") instructionData.tripType = "hourly";
    else instructionData.tripType = "one_way";

    // --------------------
    // E) Payment canonical (STRICT: ONLINE vs ONBOARD only)
    // --------------------
    // provider signals can come from several legacy fields
    const rawProvider =
        cleanStr(raw.payment?.provider) ||
        cleanStr(raw.payment?.payment_provider) ||
        cleanStr(raw.payment_provider) ||
        cleanStr(raw.payment_mode) ||
        cleanStr(raw.paymentMode) ||
        "";

    const p = rawProvider.toLowerCase();
    let provider = null;

    if (p === "paypal") provider = "paypal";
    else if (p === "stripe") provider = "stripe";
    else if (p === "revolut") provider = "revolut";
    else if (p === "squareup" || p === "square") provider = "squareup";
    else if (p === "wallet") provider = "wallet";
    else provider = null; // everything else => ONBOARD

    const paymentMode = provider ? "ONLINE" : "ONBOARD";

    const rawPaymentStatus =
        cleanStr(raw.payment?.status) ||
        cleanStr(raw.payment_status) ||
        cleanStr(raw.paymentStatus) ||
        "PENDING";

    const s = rawPaymentStatus.toUpperCase();
    let paymentStatus = "PENDING";
    if (["PAID", "PENDING", "FAILED", "CANCELLED", "REFUNDED"].includes(s)) paymentStatus = s;
    else if (["SUCCESS", "COMPLETED", "CAPTURED", "AUTHORIZED", "AUTHORISED"].includes(s)) paymentStatus = "PAID";
    else if (["CANCELED", "CANCEL", "CANCELLED"].includes(s)) paymentStatus = "CANCELLED";
    else if (["ERROR"].includes(s)) paymentStatus = "FAILED";

    // Booking status orchestration (Refined 1.3.1)
    let bookingStatus = (raw.status || "NEW").toUpperCase();

    // Map legacy/invalid statuses to NEW
    if (bookingStatus === "PAYMENT_PENDING" || bookingStatus === "PENDING" || bookingStatus === "SUCCESS" || bookingStatus === "COMPLETE") {
        bookingStatus = "NEW";
    }

    // Safety Mapping: If PAID online, ensure it's at least ACCEPTED
    if (paymentMode === "ONLINE" && paymentStatus === "PAID") {
        if (bookingStatus === "NEW") {
            bookingStatus = "ACCEPTED";
        }
    }

    // Normalized aliases
    if (bookingStatus === "CANCEL") bookingStatus = "CANCELLED";

    // --------------------
    // F) Price fallbacks
    // --------------------
    const discountAmount = parseNum(raw.discount) ?? parseNum(raw.promo_discount) ?? 0;

    const tripCost =
        parseNum(raw.trip_cost_after_discount) ??
        parseNum(raw.trip_cost) ??
        parseNum(raw.price) ??
        parseNum(raw.amount) ??
        parseNum(raw.main_price) ??
        parseNum(raw.estimate?.price) ??
        parseNum(raw.estimate?.estimateFare) ??
        parseNum(raw.trip_details?.price) ??
        0;

    const tripCostOriginal =
        parseNum(raw.trip_cost_original) ??
        (discountAmount > 0 ? Number((tripCost + discountAmount).toFixed(2)) : tripCost);

    // --------------------
    // G) Dates & reference
    // --------------------
    const createdAtValue = raw.createdAt || raw.created_at;
    const createdAtISO = toISO(createdAtValue) || new Date().toISOString();

    const createdAtMs = (() => {
        try {
            if (!createdAtValue) return null;
            if (typeof createdAtValue === "object" && typeof createdAtValue.toDate === "function") return createdAtValue.toDate().getTime();
            if (typeof createdAtValue === "number") return createdAtValue < 10_000_000_000 ? createdAtValue * 1000 : createdAtValue;
            const d = new Date(createdAtValue);
            return Number.isNaN(d.getTime()) ? null : d.getTime();
        } catch {
            return null;
        }
    })();

    const generateBookingReference = () => {
        const chars = "0123456789";
        let result = "";
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return `D2G-${result}`;
    };

    const reference =
        cleanStr(raw.reference) ||
        cleanStr(raw.booking_ref) ||
        cleanStr(raw.bookingData?.reference) ||
        generateBookingReference();

    // --------------------
    // Construct canonical object
    // --------------------
    // --- H. Duration Canonical ---
    const durationMin =
        parseNum(raw.durationMin) ??
        parseNum(raw.estimate?.tripMeta?.durationMin) ??
        parseNum(raw.estimate?.durationMin) ??
        (parseNum(raw.estimateTime) ? Math.round(parseNum(raw.estimateTime) / 60) : null) ??
        (parseNum(raw.estimate?.estimateTime) ? Math.round(parseNum(raw.estimate?.estimateTime) / 60) : null) ??
        (parseNum(raw.total_trip_time) ? Math.round(parseNum(raw.total_trip_time) / 60) : null) ??
        (parseNum(raw.durationSec) ? Math.round(parseNum(raw.durationSec) / 60) : null) ??
        null;

    const durationSec =
        parseNum(raw.durationSec) ??
        parseNum(raw.duration_sec) ??
        parseNum(raw.estimateTime) ??
        parseNum(raw.estimate?.estimateTime) ??
        (durationMin ? durationMin * 60 : null);

    const distanceKm =
        parseNum(raw.distanceKm) ??
        parseNum(raw.distance_km) ??
        parseNum(raw.estimate?.distanceKm) ??
        parseNum(raw.estimateDistance) ??
        parseNum(raw.estimate?.estimateDistance) ??
        (parseNum(raw.distance) ? (parseNum(raw.distance) > 100 ? parseNum(raw.distance) / 1000 : parseNum(raw.distance)) : null) ??
        (parseNum(raw.estimate?.distance) ? (parseNum(raw.estimate?.distance) > 100 ? parseNum(raw.estimate?.distance) / 1000 : parseNum(raw.estimate?.distance)) : null) ??
        null;

    const distance =
        parseNum(raw.distance) ??
        (distanceKm ? distanceKm * 1000 : null);

    const resolvedTripDate =
        raw.tripdate ||
        raw.tripDate ||
        raw.trip_date ||
        raw.scheduledAt ||
        raw.scheduled_at ||
        raw.instructionData?.date ||
        raw.instructionData?.scheduledAt ||
        raw.options?.date ||
        raw.options?.scheduledAt ||
        null;

    // --- CONSTRUCT CANONICAL OBJECT ---
    const canonical = {
        traceId: raw.traceId || null,
        id: raw.id || raw.bookingId || null,
        guest_token: raw.guest_token || null,
        idempotencyKey: raw.idempotencyKey || null,
        reference: reference,

        created_at: createdAtISO,
        createdAt: createdAtISO,
        updatedAt: toISO(raw.updatedAt) || new Date().toISOString(),

        bookingType: raw.bookingType || "web",
        bookingMode: raw.bookingMode || "LEGACY",
        status: bookingStatus,

        trip_status: raw.trip_status || raw.tripStatus || null,

        bookLater: raw.bookLater !== undefined ? raw.bookLater : false,

        // ✅ FIXED
        tripdate: toISO(resolvedTripDate) || new Date().toISOString(),
        tripdate_source: null, // Assuming this needs to be added, but no source provided in the instruction
        bookingDate: toISO(raw.bookingDate || raw.booking_date || resolvedTripDate || createdAtISO),
        booking_date: toISO(raw.booking_date || raw.bookingDate || resolvedTripDate || createdAtISO),

        // ✅ DURATION CANONICAL
        durationMin,
        durationSec,

        distanceKm,
        distance,
        estimateDistance: (distanceKm !== null && !isNaN(distanceKm)) ? distanceKm : (raw.estimateDistance ?? raw.estimate?.estimateDistance ?? null),
        estimateTime: (durationSec !== null && !isNaN(durationSec)) ? durationSec : ((durationMin !== null && !isNaN(durationMin)) ? durationMin * 60 : (raw.estimateTime ?? raw.estimate?.estimateTime ?? null)),
        pickupAddress: pickupAddressStr,
        dropAddress: dropAddressStr,
        pickup: pickupObj,
        drop: dropObj,
        waypoints: waypointsCanonical,
        coords: (Array.isArray(raw.coords) && raw.coords.length > 0) ? raw.coords : undefined,

        carType: raw.carType || raw.car_type || raw.carDetails?.name || raw.carImage || "ECO CLASS",

        // [V2_VEHICLE_SNAPSHOT] 
        assignedCarId: raw.assignedCarId || null,
        assignedCarType: raw.assignedCarType || raw.carType || null,
        vehicleNumber: raw.vehicleNumber || raw.vehicle_number || null,
        vehicle_number: raw.vehicle_number || raw.vehicleNumber || null,
        vehicleMake: raw.vehicleMake || null,
        vehicleModel: raw.vehicleModel || null,
        vehicleColor: raw.vehicleColor || null,
        vehicleYear: raw.vehicleYear || raw.year || null,

        trip_cost: tripCost,
        trip_cost_original: tripCostOriginal,
        driver_trip_cost: parseNum(raw.driver_trip_cost) ?? null,

        promo_code: cleanStr(raw.promo_code) || null,
        discount: parseNum(raw.discount || raw.promo_discount) || 0,
        promo_discount: parseNum(raw.promo_discount || raw.discount) || 0,
        promo_details: raw.promo_details || null,

        convenience_fees: parseNum(raw.convenience_fees) || 0,
        fleetCommission: parseNum(raw.fleetCommission) || 0,
        driver_share: parseNum(raw.driver_share) || 0,
        fleet_admin_comission: parseNum(raw.fleet_admin_comission) || 0,
        fleet_admin_fee_type: raw.fleet_admin_fee_type || null,
        currency: raw.currency || "EUR",

        pricing: {
            source: raw.pricing?.source || "ESTIMATE",
            pricingToken: raw.pricing?.pricingToken || null,
            tokenExpiry: raw.pricing?.tokenExpiry || null
        },

        // Preserve raw estimate details for duration & pricing resolution downstream
        estimate: raw.estimate || null,

        // Canonical payment
        payment: (() => {
            const isPaid = (paymentStatus === "SUCCESS" || paymentStatus === "PAID" || raw.status === "PAID");
            const wd = parseNum(raw.payment?.walletDeducted) || 0;
            const remAmt = isPaid ? 0 : (parseNum(raw.payment?.remainingAmount) ?? parseNum(raw.payment?.remaining) ?? Math.max(0, tripCost - wd));
            const isSplit = isPaid ? false : Boolean(raw.payment?.isSplitPayment || (wd > 0 && remAmt > 0));
            const amtPaid = (parseNum(raw.payment?.amountPaid) ?? parseNum(raw.amountPaid) ?? null) !== null
                ? (parseNum(raw.payment?.amountPaid) ?? parseNum(raw.amountPaid))
                : ((paymentMode === "ONBOARD" && !isPaid) ? wd : tripCost);

            return {
                mode: paymentMode,                 // ONLINE | ONBOARD
                provider: provider,                // paypal|stripe|revolut|squareup|wallet | null
                status: paymentStatus,             // PENDING|PAID|FAILED|CANCELLED|REFUNDED
                amountPaid: amtPaid,
                walletDeducted: wd,
                remainingAmount: remAmt,
                remaining: remAmt,
                isSplitPayment: isSplit,
                attemptId:
                    cleanStr(raw.payment?.attemptId) ||
                    cleanStr(raw.payment?.attempt) ||
                    cleanStr(raw.idempotencyKey) ||
                    null,
                transaction_id:
                    cleanStr(raw.payment?.transaction_id) ||
                    cleanStr(raw.payment?.transactionId) ||
                    cleanStr(raw.transaction_id) ||
                    cleanStr(raw.transactionId) ||
                    null
            };
        })(),

        // Legacy compatibility (do NOT use for logic in new code)
        payment_mode: paymentMode === "ONLINE" ? provider : "onboard",
        payment_status: paymentStatus,

        // Roles
        customer,
        customer_uid: customer.uid,
        booker_details,
        is_booking_for_other: isBookingForOther,
        booked_by_admin: Boolean(raw.booked_by_admin),
        admin_details: raw.admin_details ? {
            uid: cleanStr(raw.admin_details.uid),
            firstName: cleanStr(raw.admin_details.firstName || raw.admin_details.name),
            lastName: cleanStr(raw.admin_details.lastName),
            email: cleanStr(raw.admin_details.email)
        } : null,

        // Legacy customer mirror
        customer_name: (cleanStr(customer.firstName) + " " + cleanStr(customer.lastName)).trim(),
        customer_email: customer.email,
        customer_contact: customer.phone,
        customer_image: customer.image || raw.customer_image || "",
        customer_info_updated_at: raw.customer_info_updated_at || raw.customer?.customer_info_updated_at || null,

        // Driver
        driver: raw.driver
            ? (typeof raw.driver === 'string'
                ? {
                    uid: raw.driver,
                    name: raw.driver_name || "",
                    phone: raw.driver_contact || null,
                    vehicleModel: raw.driver_vehicle_model || null,
                    vehicleNumber: raw.driver_vehicle_number || null,
                    image: raw.driver_image || null,
                    assignedAt: raw.driver_assigned_at || null
                }
                : {
                    uid: raw.driver.uid,
                    name: raw.driver.name,
                    phone: raw.driver.phone || null,
                    vehicleModel: raw.driver.vehicleModel || null,
                    vehicleNumber: raw.driver.vehicleNumber || null,
                    image: raw.driver.image || null,
                    assignedAt: raw.driver.assignedAt || null
                })
            : null,

        driver_name: (raw.driver && raw.driver.name) || raw.driver_name || "",
        driver_image: (raw.driver && raw.driver.image) || raw.driver_image || "",
        driver_contact: (raw.driver && raw.driver.phone) || raw.driver_contact || "",
        driver_email: (raw.driver && raw.driver.email) || raw.driver_email || "",

        instructionData: {
            tripType: instructionData.tripType || raw.tripType || "one_way",
            driverNotes: instructionData.driverNotes || raw.driverNotes || raw.notes || "",
            options: Array.isArray(instructionData.options) ? instructionData.options : [],

            returnDate: instructionData.returnDate || raw.returnDate || null,
            flightNumber: instructionData.flightNumber || raw.flightNumber || null,

            pickupFlightNumber: instructionData.pickupFlightNumber || raw.pickupFlightNumber || raw.flight_no || null,
            pickupTrainNumber: instructionData.pickupTrainNumber || raw.pickupTrainNumber || raw.train_no || null,

            hours: instructionData.hours || raw.hours || 0,
            waiting_time: instructionData.waiting_time || raw.waiting_time || 0,

            passengers: instructionData.passengers || raw.passengers || raw.passengerCount || 1,
            luggage: instructionData.luggage || raw.luggage || raw.luggageCount || 0,

            baby_seat: instructionData.baby_seat || raw.baby_seat || raw.babySeat || 0,
            child_seat: instructionData.child_seat || raw.child_seat || raw.childSeat || 0,
            booster_seat: instructionData.booster_seat || raw.booster_seat || raw.boosterSeat || 0,
            // [FIX] Whitelist isAirport to ensure backend receives it
            isAirport: instructionData.isAirport || raw.isAirport || false
        },

        pickupFlightNumber: instructionData.pickupFlightNumber || raw.pickupFlightNumber || raw.flight_no || null,
        pickupTrainNumber: instructionData.pickupTrainNumber || raw.pickupTrainNumber || raw.train_no || null,

        returnTrip: returnTripCanonical,

        // Round trip & Legs metadata
        leg: raw.leg || (isReturnLeg ? "return" : (returnTripCanonical?.enabled ? "outbound" : null)),
        isReturn: isReturnLeg,
        hasReturn: raw.hasReturn !== undefined ? raw.hasReturn : Boolean(returnTripCanonical?.enabled || isReturnLeg),
        legLabel: raw.legLabel || (isReturnLeg ? "RETOUR" : (raw.leg === "outbound" ? "ALLER" : null)),
        bookingId: raw.bookingId || raw.id || null,
        bundleId: raw.bundleId || null,
        bundleTotalCost: parseNum(raw.bundleTotalCost) ?? null,
        bundleTripCostOriginal: parseNum(raw.bundleTripCostOriginal) ?? null,
        bundleOriginalTotal: parseNum(raw.bundleOriginalTotal) ?? null,
        bundleDiscount: parseNum(raw.bundleDiscount) ?? null,
        linkedBookingId: raw.linkedBookingId || raw.returnTrip?.linkedBookingId || null,
        legTripCost: parseNum(raw.legTripCost) ?? null,
        legTripCostOriginal: parseNum(raw.legTripCostOriginal) ?? null,
        legDiscount: parseNum(raw.legDiscount) ?? null,
        roundTripTotal: raw.roundTripTotal !== undefined ? raw.roundTripTotal : null,
        outboundTripDate: raw.outboundTripDate || (isReturnLeg ? null : raw.tripdate) || null,
        returnTripDate: raw.returnTripDate || raw.returnTrip?.tripdate || raw.returnDate || null,

        emailPremiumSent: raw.emailPremiumSent || false,
        emailPremiumSentAt: raw.emailPremiumSentAt || null,
        emailError: raw.emailError || null,

        // Cancellation info
        reason: (() => {
            let r = cleanStr(raw.reason || raw.cancel_reason || raw.cancelReason?.reason || "");
            if (r.startsWith('[missing') && r.includes('translation]')) {
                const match = r.match(/"(?:[a-z]{2}\.)?([^"]+)"/);
                if (match && match[1]) {
                    r = match[1].replace(/_/g, ' ');
                    r = r.charAt(0).toUpperCase() + r.slice(1);
                }
            }
            return r;
        })(),
        cancelledBy: cleanStr(raw.cancelledBy || raw.cancel_by || raw.cancelReason?.by || ""),
        cancelledAt: toISO(raw.cancelledAt || raw.cancel_at) || null,

        // Action timestamps
        driverAssignedAt: toISO(raw.driverAssignedAt) || null,
        completedAt: toISO(raw.completedAt) || null,

        // Billing & Invoicing (Preserve)
        billing: raw.billing || null,
        invoiceIssued: raw.invoiceIssued || raw.billing?.invoiceIssued || false,
        invoiceNumber: raw.invoiceNumber || raw.billing?.invoiceNumber || null,
        invoiceId: raw.invoiceId || raw.billing?.invoiceId || null,

        language: raw.language || "en"
    };

    return stripUndefinedDeep(canonical);
};